import { useParams } from "react-router-dom";
import { getTeacherHandover } from "../api.js";
import { useAsync } from "../useAsync.js";

function formatDate(value: string): string {
  const date = new Date(value + "T00:00:00");
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "full",
    timeZone: "Europe/London",
  }).format(date);
}

function formatLesson(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  }).format(new Date(value));
}

export function TeacherHandoverPage() {
  const { id = "" } = useParams();
  const handover = useAsync(() => getTeacherHandover(id), [id]);

  if (handover.loading && !handover.data) return <p role="status">Loading handover…</p>;
  if (handover.error || !handover.data) {
    return (
      <section className="panel" aria-labelledby="handover-unavailable-title">
        <h1 id="handover-unavailable-title">Handover unavailable</h1>
        <p role="alert">{handover.error ?? "This handover is unavailable or has been revoked."}</p>
      </section>
    );
  }

  const item = handover.data;
  return (
    <section className="panel" aria-labelledby="teacher-handover-title">
      <h1 id="teacher-handover-title">
        {item.studentName} · {item.instrument} teaching handover
      </h1>
      <p>
        Planned effective date: {formatDate(item.effectiveDate)}. From {item.sourceTutorName} to{" "}
        {item.destinationTutorName}.
      </p>
      <p>
        {item.assignmentTransfer === "effective"
          ? "The instrument teaching relationship has transferred to " + item.destinationTutorName + "."
          : item.assignmentTransfer === "scheduled"
            ? "The instrument teaching relationship is scheduled to transfer on " + formatDate(item.effectiveDate) + "."
            : item.assignmentTransfer === "cancelled"
              ? "The scheduled instrument transfer was cancelled."
              : "No teaching assignment transfer was scheduled."}
        {" "}Provider appointments, credits and Inbox history are not moved by this handover.
      </p>
      <section aria-labelledby="handover-summary-title">
        <h2 id="handover-summary-title">Tutor summary</h2>
        <p>{item.summary}</p>
      </section>
      <section aria-labelledby="handover-goals-title">
        <h2 id="handover-goals-title">Current goals</h2>
        {item.context.goals.length === 0 && <p>No selected current goals remain available.</p>}
        <ul>
          {item.context.goals.map((goal) => (
            <li key={goal.id}>
              <strong>{goal.title}</strong>
              {goal.description && <p>{goal.description}</p>}
              {goal.targetDate && <p>Target date: {formatDate(goal.targetDate)}</p>}
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="handover-repertoire-title">
        <h2 id="handover-repertoire-title">Current repertoire</h2>
        {item.context.repertoire.length === 0 && <p>No selected current repertoire remains available.</p>}
        <ul>
          {item.context.repertoire.map((entry) => (
            <li key={entry.id}>
              {entry.title}
              {entry.composer ? " — " + entry.composer : ""}
              {entry.instrument ? " · " + entry.instrument : ""}
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="handover-practice-title">
        <h2 id="handover-practice-title">Unfinished practice tasks</h2>
        {item.context.practiceTasks.length === 0 && <p>No selected unfinished practice tasks remain available.</p>}
        <ul>
          {item.context.practiceTasks.map((task) => (
            <li key={task.id}>
              <strong>{task.title}</strong>
              {task.description && <p>{task.description}</p>}
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="handover-lessons-title">
        <h2 id="handover-lessons-title">Upcoming confirmed lessons</h2>
        <p>These are current Hub mirror records. This handover has not moved them with the booking provider.</p>
        {item.context.upcomingLessons.length === 0 && <p>No selected upcoming lessons remain available.</p>}
        <ul>
          {item.context.upcomingLessons.map((lesson) => (
            <li key={lesson.id}>
              {formatLesson(lesson.startsAt)} · {lesson.serviceName ?? "Lesson"}
              {lesson.tutorName ? " · currently with " + lesson.tutorName : ""}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
